import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Receipt, DollarSign, Calendar } from "lucide-react";
import { useCurrency } from "@/hooks/use-currency";
import type { Currency } from "@/lib/currency-conversion";

interface ProjectPaymentsProps {
  projectId: string;
}

export function ProjectPayments({ projectId }: ProjectPaymentsProps) {
  const { formatAmount } = useCurrency();

  const { data: payments, isLoading } = useQuery({
    queryKey: ["project-payments", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("project_id", projectId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-20 bg-muted animate-pulse rounded-lg" />
        <div className="h-20 bg-muted animate-pulse rounded-lg" />
      </div>
    );
  }

  if (!payments || payments.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-10">
          <Receipt className="h-12 w-12 text-muted-foreground mb-3" />
          <p className="text-muted-foreground text-center">No payments recorded for this project yet</p>
        </CardContent>
      </Card>
    );
  }

  const totalAmount = payments.reduce((sum, payment) => sum + payment.price, 0);

  return (
    <div className="space-y-4">
      <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Total Revenue
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-3xl font-bold">
            {formatAmount(totalAmount, (payments[0]?.currency || "USD") as Currency)}
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            {payments.length} payment{payments.length !== 1 ? "s" : ""} recorded
          </p>
        </CardContent>
      </Card>

      <div className="space-y-3">
        {payments.map((payment) => (
          <Card key={payment.id}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <CardTitle className="text-base">{payment.plan_name}</CardTitle>
                  <CardDescription className="flex items-center gap-2">
                    <Calendar className="h-3 w-3" />
                    {new Date(payment.created_at).toLocaleDateString()}
                  </CardDescription>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold">
                    {formatAmount(payment.price, (payment.currency || "USD") as Currency)}
                  </p>
                  <Badge variant="outline" className="mt-1">
                    {payment.payment_type === "recurring" ? "Recurring" : "One-Time"}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex items-center justify-between text-sm">
                <div className="space-y-1">
                  {payment.client_name && (
                    <p className="text-muted-foreground">Client: {payment.client_name}</p>
                  )}
                  {payment.transaction_id && (
                    <p className="text-muted-foreground font-mono text-xs">
                      Ref: {payment.transaction_id}
                    </p>
                  )}
                </div>
                <Badge
                  variant="secondary"
                  className={
                    payment.payment_status === "completed"
                      ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                      : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                  }
                >
                  {payment.payment_status}
                </Badge>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
